import { createHmac, timingSafeEqual } from 'node:crypto';
import type { PrismaClient } from '@prisma/client';
import express, { Router } from 'express';
import type { Config } from '../../core/config.js';
import { asyncHandler } from '../../core/http.js';

/**
 * WhatsApp Business Cloud API adapter (optional). When the four WHATSAPP_* settings are present, the Send
 * dialog's WhatsApp channel sends by API and records the provider message id so delivery and read receipts
 * (webhook) can update the communication log. Without them the app keeps the click-to-chat link and logs
 * "shared (unconfirmed)". Free-form text only reaches people who messaged you in the last 24 hours;
 * outside that window WhatsApp requires an approved template (not built here).
 */
export type WhatsAppConfig = Pick<Config, 'WHATSAPP_CLOUD_TOKEN' | 'WHATSAPP_PHONE_NUMBER_ID'>;

export const whatsappCloudEnabled = (c: Partial<WhatsAppConfig>) =>
  !!(c.WHATSAPP_CLOUD_TOKEN && c.WHATSAPP_PHONE_NUMBER_ID);

type FetchLike = typeof fetch;
let fetchImpl: FetchLike | null = null;
export const setWhatsAppFetchForTests = (f: FetchLike | null) => {
  fetchImpl = f;
};

export async function sendWhatsAppCloudText(
  c: Partial<WhatsAppConfig>,
  to: string,
  body: string,
): Promise<{ messageId: string }> {
  const res = await (fetchImpl ?? fetch)(
    `https://graph.facebook.com/v20.0/${c.WHATSAPP_PHONE_NUMBER_ID}/messages`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${c.WHATSAPP_CLOUD_TOKEN}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messaging_product: 'whatsapp',
        to: to.replace(/^\+/, ''),
        type: 'text',
        text: { preview_url: true, body },
      }),
    },
  );
  const json = (await res.json().catch(() => ({}))) as {
    messages?: { id: string }[];
    error?: { message?: string };
  };
  if (!res.ok || !json.messages?.[0]?.id)
    throw new Error(json.error?.message ?? `WhatsApp API error (${res.status})`);
  return { messageId: json.messages[0].id };
}

const STATUS_MAP: Record<string, string> = {
  sent: 'sent',
  delivered: 'delivered',
  read: 'read',
  failed: 'failed',
};
const RANK: Record<string, number> = { queued: 0, sent: 1, delivered: 2, read: 3, failed: 9 };

/** Webhook: GET verification handshake and POST status updates (signature checked on the raw body). */
export function createWhatsAppWebhookRouter({ db, config }: { db: PrismaClient; config: Config }) {
  const router = Router();
  router.get('/api/v1/webhooks/whatsapp', (req, res) => {
    if (
      config.WHATSAPP_VERIFY_TOKEN &&
      req.query['hub.mode'] === 'subscribe' &&
      req.query['hub.verify_token'] === config.WHATSAPP_VERIFY_TOKEN
    ) {
      res.status(200).send(String(req.query['hub.challenge'] ?? ''));
      return;
    }
    res.status(403).end();
  });
  router.post(
    '/api/v1/webhooks/whatsapp',
    express.raw({ type: '*/*', limit: '200kb' }),
    asyncHandler(async (req, res) => {
      const raw = Buffer.isBuffer(req.body) ? req.body : Buffer.from('');
      const secret = config.WHATSAPP_APP_SECRET;
      if (!secret) return void res.status(403).end();
      const given = String(req.headers['x-hub-signature-256'] ?? '').replace(/^sha256=/, '');
      const expected = createHmac('sha256', secret).update(raw).digest('hex');
      const a = Buffer.from(given.padEnd(expected.length, '0').slice(0, expected.length));
      if (given.length !== expected.length || !timingSafeEqual(a, Buffer.from(expected)))
        return void res.status(403).end();
      let payload: {
        entry?: {
          changes?: {
            value?: { statuses?: { id: string; status: string; errors?: { title?: string }[] }[] };
          }[];
        }[];
      };
      try {
        payload = JSON.parse(raw.toString('utf8'));
      } catch {
        return void res.status(400).end();
      }
      for (const e of payload.entry ?? [])
        for (const c of e.changes ?? [])
          for (const s of c.value?.statuses ?? []) {
            const status = STATUS_MAP[s.status];
            if (!status || !s.id) continue;
            const comm = await db.communication.findFirst({
              where: { externalId: s.id, channel: 'whatsapp' },
            });
            if (!comm) continue;
            // Never move backwards (a late "sent" must not undo "read").
            if ((RANK[status] ?? 0) < (RANK[comm.status] ?? 0) && status !== 'failed') continue;
            await db.communication.update({
              where: { id: comm.id },
              data: {
                status,
                ...(status === 'failed'
                  ? { error: s.errors?.[0]?.title?.slice(0, 300) ?? 'Failed' }
                  : {}),
              },
            });
          }
      res.status(200).json({ ok: true });
    }),
  );
  return router;
}
